/**
 * Popular Posts Component
 * Displays popular posts from the same suburb/region
 * Danggeun-style popular posts section
 */

import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { normalizeImageUrl } from "@/lib/image-url";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { MapPin, Eye, Heart, MessageCircle, Flame } from "lucide-react";
import { Link } from "wouter";
import { communityTalkCategories } from "@shared/schema";

interface PopularPost {
  id: string | number;
  title: string;
  content: string;
  imageUrls: string[] | null;
  suburb: string | null;
  category: string;
  views: number;
  likes: number;
  commentCount: number;
  createdAt: string;
  authorNickname: string | null;
  score: number;
}

interface PopularPostsProps {
  suburb?: string | null;
  excludeId?: string | number;
  limit?: number;
  hours?: number;
  showHeader?: boolean;
  className?: string;
}

export function PopularPosts({
  suburb,
  excludeId,
  limit = 5,
  hours = 24,
  showHeader = true,
  className = "",
}: PopularPostsProps) {
  const { data, isLoading, error } = useQuery<{
    posts: PopularPost[];
    count: number;
    suburb: string | null;
    hours: number;
  }>({
    queryKey: ["/api/community-talk/popular", suburb, excludeId, limit, hours],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        hours: hours.toString(),
      });

      if (suburb) {
        params.append("suburb", suburb);
      }

      if (excludeId) {
        params.append("exclude_id", String(excludeId));
      }

      const response = await fetch(
        `/api/community-talk/popular?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch popular posts");
      }

      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: true, // Always fetch, even without suburb
  });

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        {showHeader && (
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
        )}
        {Array.from({ length: limit }).map((_, i) => (
          <Card key={i} className="p-4">
            <div className="space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-32 w-full rounded-lg" />
              <div className="flex gap-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error || !data || data.posts.length === 0) {
    return null; // Don't show anything if no popular posts
  }

  const posts = data.posts;
  const suburbName = suburb || "동네";

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500" />
            <h3 className="text-lg font-semibold">
              {suburbName} 인기글
            </h3>
          </div>
          <Link href="/community-talk">
            <span className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
              더보기 →
            </span>
          </Link>
        </div>
      )}

      {/* Popular Posts List */}
      <div className="space-y-3">
        {posts.map((post) => {
          const categoryInfo = communityTalkCategories.find(
            (c) => c.id === post.category
          );
          const categoryEmoji =
            categoryInfo && "emoji" in categoryInfo && typeof categoryInfo.emoji === "string"
              ? categoryInfo.emoji
              : null;
          const hasImage = post.imageUrls && post.imageUrls.length > 0;
          const firstImage = hasImage ? post.imageUrls[0] : null;
          const normalizedImage = firstImage
            ? normalizeImageUrl(firstImage)
            : null;

          // Truncate content for preview
          const contentPreview =
            post.content.length > 100
              ? post.content.substring(0, 100) + "..."
              : post.content;

          return (
            <Link key={post.id} href={`/community-talk/${post.id}`}>
              <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                <div className="flex gap-4">
                  {/* Image or Category Icon */}
                  <div className="shrink-0">
                    {normalizedImage ? (
                      <img
                        src={normalizedImage}
                        alt={post.title}
                        className="w-20 h-20 rounded-lg object-cover"
                        onError={(e) => {
                          e.currentTarget.src =
                            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23e5e7eb" width="80" height="80"/%3E%3C/svg%3E';
                        }}
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center">
                        {categoryEmoji ? (
                          <span className="text-2xl">{categoryEmoji}</span>
                        ) : (
                          <MessageCircle className="h-8 w-8 text-muted-foreground" />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 space-y-2">
                    {/* Category Badge */}
                    {categoryInfo && (
                      <Badge variant="outline" className="text-xs">
                        {categoryInfo.label}
                      </Badge>
                    )}

                    {/* Title */}
                    <h4 className="font-semibold text-sm line-clamp-2">
                      {post.title}
                    </h4>

                    {/* Content Preview */}
                    {contentPreview && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {contentPreview}
                      </p>
                    )}

                    {/* Meta Info */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {post.suburb && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          <span>{post.suburb}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        <span>{post.views}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart className="h-3 w-3" />
                        <span>{post.likes}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageCircle className="h-3 w-3" />
                        <span>{post.commentCount}</span>
                      </div>
                      <span>
                        {formatDistanceToNow(new Date(post.createdAt), {
                          addSuffix: true,
                          locale: ko,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
